import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/*
  A little floating status pill that follows you across the video pages.
  Because generation runs as a background job on the server, closing the tab
  never stops it — this just lets any page SEE what's still cooking, and flash
  a "ready" note when something finishes while you were away.

  Drop <VideoBanner /> anywhere inside a page (it fixes itself to the bottom).
*/
export default function VideoBanner() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [count, setCount] = useState(0);
  const [justFinished, setJustFinished] = useState(false);
  const prevCount = useRef(0);
  const finishTimer = useRef(null);

  useEffect(() => {
    let alive = true;

    const check = async () => {
      try {
        const res = await axios.get(`${API}/video/active?token=${token}`);
        if (!alive) return;
        const n = res.data.count || 0;

        // count dropped -> at least one clip just finished
        if (n < prevCount.current) {
          setJustFinished(true);
          clearTimeout(finishTimer.current);
          finishTimer.current = setTimeout(() => setJustFinished(false), 8000);
        }
        prevCount.current = n;
        setCount(n);
      } catch (e) {
        // stay quiet on errors — the banner is a nicety, not critical
      }
    };

    check();
    const t = setInterval(check, 5000);
    return () => {
      alive = false;
      clearInterval(t);
      clearTimeout(finishTimer.current);
    };
  }, [token]);

  // nothing in flight and nothing to announce -> render nothing
  if (count === 0 && !justFinished) return null;

  const generating = count > 0;

  const s = {
    wrap: {
      position: 'fixed', left: '50%', bottom: '24px', transform: 'translateX(-50%)',
      zIndex: 200, width: 'calc(100% - 48px)', maxWidth: '440px',
    },
    pill: {
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '13px 18px', borderRadius: '16px', cursor: 'pointer',
      background: generating
        ? 'linear-gradient(135deg, #2a2033, #3a2a1a)'
        : 'linear-gradient(135deg, #8a5f35, #c9935a)',
      border: '1px solid rgba(201,147,90,0.28)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: generating ? '#e8ddd0' : '#0e0b0f',
    },
    dot: {
      width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
      background: generating ? '#c9935a' : '#0e0b0f',
    },
    text: { flex: 1, fontSize: '13.5px' },
    action: { fontSize: '12px', opacity: 0.85, flexShrink: 0 },
  };

  return (
    <div style={s.wrap}>
      <style>{`
        @keyframes vbpulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>
      <div style={s.pill} onClick={() => navigate('/video/library')} role="button" tabIndex={0}>
        <div style={{ ...s.dot, animation: generating ? 'vbpulse 1.4s ease-in-out infinite' : 'none' }} />
        <div style={s.text}>
          {generating
            ? `${count} video${count > 1 ? 's' : ''} generating…`
            : 'video ready ♡'}
        </div>
        <div style={s.action}>view →</div>
      </div>
    </div>
  );
}
